import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { 
  FaHtml5, 
  FaCss3Alt, 
  FaJs, 
  FaReact, 
  FaNodeJs, 
  FaPhp, 
  FaWordpress, 
  FaFigma, 
  FaLaravel
} from 'react-icons/fa';
import { SiTailwindcss, SiMongodb, SiMysql } from 'react-icons/si';

const AboutSection = styled.section`
  background-color: #f8fafc;
  padding: 100px 0;
  position: relative;
  overflow: hidden;
`;

const AboutContainer = styled.div`
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const SectionSubtitle = styled(motion.span)`
  display: inline-block;
  font-size: 1rem;
  font-weight: 600;
  color: #3b82f6;
  margin-bottom: 12px;
  position: relative;
  
  &:before, &:after {
    content: '';
    position: absolute;
    top: 50%;
    height: 1px;
    width: 30px;
    background-color: #3b82f6;
    transform: translateY(-50%);
  }
  
  &:before {
    right: calc(100% + 10px);
  }
  
  &:after {
    left: calc(100% + 10px);
  }
`;

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
  
  span {
    color: #3b82f6;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionDescription = styled(motion.p)`
  font-size: 1.1rem;
  color: #64748b;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
`;

const AboutContent = styled.div`
  display: flex;
  align-items: center;
  gap: 60px;
  margin-top: 60px;
  
  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const AboutImageContainer = styled.div`
  flex: 1;
  position: relative;
`;

const AboutImageWrapper = styled(motion.div)`
  position: relative;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: 40px;
    bottom: 40px;
    background: linear-gradient(135deg, #3b82f6, #0e7490);
    z-index: -1;
    border-radius: 10px;
  }
  
  img {
    width: 100%;
    border-radius: 10px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const AboutTextContent = styled.div`
  flex: 1;
`;

const AboutTitle = styled(motion.h3)`
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 20px;
`;

const AboutDescription = styled(motion.p)`
  font-size: 1rem;
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 30px;
`;

const AboutFeatures = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
  margin-bottom: 30px;
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 15px;
`;

const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
`;

const FeatureText = styled.div`
  h4 {
    font-size: 1.1rem;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 5px;
  }
  
  p {
    font-size: 0.95rem;
    color: #64748b;
    line-height: 1.6;
  }
`;

const SkillsContainer = styled(motion.div)`
  margin-top: 40px;
`;

const SkillsTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SkillItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 15px;
  border-radius: 8px;
  background-color: white;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  svg {
    font-size: 2rem;
    margin-bottom: 10px;
    transition: all 0.3s ease;
  }
  
  span {
    font-size: 0.9rem;
    color: #1e293b;
    font-weight: 500;
  }
  
  &:hover {
    transform: translateY(-5px);
    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
    
    svg, span {
      color: white;
    }
  }
`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// Skills component with original brand colors
const SkillIcon = ({ icon: Icon, name, color }) => {
  return (
    <SkillItem>
      <Icon style={{ color }} />
      <span>{name}</span>
    </SkillItem>
  );
};

// Stats component
const StatItem = styled(motion.div)`
  text-align: center;
  padding: 30px 20px;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
  
  h3 {
    font-size: 2.5rem;
    font-weight: 700;
    color: #0e7490;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 1rem;
    color: #64748b;
    font-weight: 500;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 60px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const About = () => {
  const skills = [
    { icon: FaHtml5, name: 'HTML5', color: '#E34F26' },
    { icon: FaCss3Alt, name: 'CSS3', color: '#1572B6' },
    { icon: FaJs, name: 'JavaScript', color: '#F7DF1E' },
    { icon: FaReact, name: 'React', color: '#61DAFB' },
    { icon: FaNodeJs, name: 'Node.js', color: '#339933' },
    { icon: SiTailwindcss, name: 'Tailwind', color: '#06B6D4' },
    { icon: SiMongodb, name: 'MongoDB', color: '#47A248' },
    { icon: FaLaravel, name: 'Laravel', color: '#F24E1E' },
    { icon: SiMysql, name: 'MySQL', color: '#F24E1E' },
    { icon: FaPhp, name: 'PHP', color: '#777BB4' },
    { icon: FaWordpress, name: 'WordPress', color: '#21759B' },
    { icon: FaFigma, name: 'Figma', color: '#F24E1E' }
  ];

  return (
    <AboutSection id="about">
      <AboutContainer>
        <SectionHeader
          as={motion.div}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <SectionSubtitle variants={fadeInUp}>About Websigma</SectionSubtitle>
          <SectionTitle variants={fadeInUp}>
            We'd love to help you <span>elevate your digital experience</span>
          </SectionTitle>
          <SectionDescription variants={fadeInUp}>
            Celebrating our 10th year in business, we have been at the forefront of the Technology services & digital marketing revolution, helping businesses of all sizes thrive in the digital age.
          </SectionDescription>
        </SectionHeader>
        
        <StatsContainer>
          <StatItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3>90%</h3>
            <p>Client retention</p>
          </StatItem>
          
          <StatItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h3>1M+</h3>
            <p>Users reached</p>
          </StatItem>
          
          <StatItem
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h3>10</h3>
            <p>A decade in the industry</p>
          </StatItem>
        </StatsContainer>
        
        <AboutContent>
          <AboutImageContainer
            as={motion.div}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
          >
            <AboutImageWrapper>
              <img 
                src={`${process.env.PUBLIC_URL}/assets/images/websigma-team.jpg`} 
                alt="Websigma Team"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80";
                }}
              />
            </AboutImageWrapper>
          </AboutImageContainer>
          
          <AboutTextContent
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            <AboutTitle variants={fadeInUp}>
              Welcome to WebSigma, where innovation meets excellence
            </AboutTitle>
            
            <AboutDescription variants={fadeInUp}>
              Our journey began a decade ago with a simple mission: to empower businesses to connect with their audiences in meaningful ways. Over the years, we've grown from a small team of passionate marketers to a full-fledged digital powerhouse, offering a comprehensive suite of services designed to elevate your brand and drive results.
            </AboutDescription>
            
            <AboutFeatures variants={fadeInUp}>
              <FeatureItem>
                <FeatureIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M9.5 2.672a.5.5 0 1 0 1 0V1.5h4a.5.5 0 0 1 .5.5v4.8a.5.5 0 0 1-.5.5h-1.172a.5.5 0 0 0 0 1H15a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1h-5a1 1 0 0 0-1 1v1.172a.5.5 0 0 0 .5.5zm1 2.165a.5.5 0 0 0 1 0V4.5h4a.5.5 0 0 1 .5.5v4.8a.5.5 0 0 1-.5.5h-1.172a.5.5 0 0 0 0 1H16a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1h-5a1 1 0 0 0-1 1v1.172a.5.5 0 0 0 .5.5z"/>
                    <path d="M1 9.5A1.5 1.5 0 0 1 2.5 8h5A1.5 1.5 0 0 1 9 9.5v5A1.5 1.5 0 0 1 7.5 16h-5A1.5 1.5 0 0 1 1 14.5v-5zm1.5-.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5h-5z"/>
                  </svg>
                </FeatureIcon>
                <FeatureText>
                  <h4>Responsive Design</h4>
                  <p>Websites that look and work perfectly on all devices</p>
                </FeatureText>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm7.5-6.923c-.67.204-1.335.82-1.887 1.855A7.97 7.97 0 0 0 5.145 4H7.5V1.077zM4.09 4a9.267 9.267 0 0 1 .64-1.539 6.7 6.7 0 0 1 .597-.933A7.025 7.025 0 0 0 2.255 4H4.09zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a6.958 6.958 0 0 0-.656 2.5h2.49zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5H4.847zM8.5 5v2.5h2.99a12.495 12.495 0 0 0-.337-2.5H8.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5H4.51zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5H8.5zM5.145 12c.138.386.295.744.468 1.068.552 1.035 1.218 1.65 1.887 1.855V12H5.145zm.182 2.472a6.696 6.696 0 0 1-.597-.933A9.268 9.268 0 0 1 4.09 12H2.255a7.024 7.024 0 0 0 3.072 2.472zM3.82 11a13.652 13.652 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5H3.82zm6.853 3.472A7.024 7.024 0 0 0 13.745 12H11.91a9.27 9.27 0 0 1-.64 1.539 6.688 6.688 0 0 1-.597.933zM8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855.173-.324.33-.682.468-1.068H8.5zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.65 13.65 0 0 1-.312 2.5zm2.802-3.5a6.959 6.959 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5h2.49zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7.024 7.024 0 0 0-3.072-2.472c.218.284.418.598.597.933zM10.855 4a7.966 7.966 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4h2.355z"/>
                  </svg>
                </FeatureIcon>
                <FeatureText>
                  <h4>SEO Optimization</h4>
                  <p>Improve your visibility on search engines</p>
                </FeatureText>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M11 2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v12h.5a.5.5 0 0 1 0 1H.5a.5.5 0 0 1 0-1H1v-3a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3h1V7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7h1V2zm1 12h2V2h-2v12zm-3 0V7H7v7h2zm-5 0v-3H2v3h2z"/>
                  </svg>
                </FeatureIcon>
                <FeatureText>
                  <h4>Performance</h4>
                  <p>Fast loading websites that keep users engaged</p>
                </FeatureText>
              </FeatureItem>
              
              <FeatureItem>
                <FeatureIcon>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zM0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8z"/>
                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/>
                  </svg>
                </FeatureIcon>
                <FeatureText>
                  <h4>Support & Maintenance</h4>
                  <p>Ongoing support to keep your website up-to-date</p>
                </FeatureText>
              </FeatureItem>
            </AboutFeatures>
            
            <SkillsContainer variants={fadeInUp}>
              <SkillsTitle>Technologies We Work With</SkillsTitle>
              <SkillsGrid>
                {skills.map((skill, index) => (
                  <SkillIcon 
                    key={index} 
                    icon={skill.icon} 
                    name={skill.name} 
                    color={skill.color} 
                  />
                ))}
              </SkillsGrid>
            </SkillsContainer>
          </AboutTextContent>
        </AboutContent>
      </AboutContainer>
    </AboutSection>
  );
};

export default About;
